import { useQuery, useQueryClient } from '@tanstack/react-query'

import { entitlementsApi } from '@/features/entitlements/api'
import { useAuth } from '@/features/auth/hooks/useAuth'
import type { ModuleCode } from '@/features/entitlements/types'

// Sección 35: cache razonable, NUNCA indefinido — un cambio de plan
// (SA-6/SA-7, hecho por el operador de plataforma) debe propagarse sin
// requerir un nuevo login. 2 minutos de staleTime es un compromiso: no
// dispara una consulta nueva en cada render, pero tampoco deja al
// usuario con una vista de módulos obsoleta por mucho tiempo.
const ENTITLEMENTS_STALE_MS = 2 * 60_000

export function useEntitlements() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: ['entitlements', 'me'],
    queryFn: () => entitlementsApi.getMine(),
    enabled: isAuthenticated,
    staleTime: ENTITLEMENTS_STALE_MS,
    refetchOnWindowFocus: true,
  })
}

export function useInvalidateEntitlements() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['entitlements', 'me'] })
}

/**
 * Sección 4/22: sin gestión comercial (`commercially_managed=false`,
 * organización legacy — incluye Irapuato hoy) → SIEMPRE true, el RBAC ya
 * existente sigue siendo la única puerta. Con gestión comercial →
 * `modules` es la única fuente de verdad, `[]` es CERO módulos, nunca
 * "todos". Mismo criterio exacto que
 * app.commercial.entitlement_guard.is_module_enabled_for_org — no se
 * reimplementa distinto en frontend, solo se refleja el mismo resultado
 * que el backend ya calculó.
 */
export function hasModule(entitlements: { commercially_managed: boolean; modules: string[] } | undefined, module: ModuleCode): boolean {
  if (!entitlements) return true // aún cargando / sin datos: nunca ocultar de más antes de tener certeza
  if (!entitlements.commercially_managed) return true
  return entitlements.modules.includes(module)
}
